import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, MailCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Nav";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SIDE = "https://images.unsplash.com/photo-1728349044554-6382c288fae7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const [form, setForm] = useState({ name: "", identifier: "" });
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const request = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/otp/request", form);
      setSent(data); setCode(""); setCooldown(30);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/otp/verify", { identifier: form.identifier, code });
      setUser(data);
      nav(sp.get("next") || (isLogin ? "/dashboard" : "/new"));
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex flex-col px-6 py-8 sm:px-12">
        <Logo />
        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-12 rise">
          <p className="overline">{isLogin ? "Welcome back" : "Begin a new book"}</p>
          <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl">{isLogin ? "Log in" : "Create your account"}</h1>
          <p className="mt-3 text-sm text-[#5E5868]">No passwords. We'll send a one-time code to your email or mobile number.</p>

          {!sent ? (
            <form onSubmit={request} className="mt-10 space-y-5" data-testid="auth-form">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name">Your name</Label>
                  <Input id="name" data-testid="register-name-input" value={form.name} onChange={set("name")} className="h-11 bg-white" />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="identifier">Email or mobile number</Label>
                <Input id="identifier" type="text" inputMode="email" autoComplete="username" required autoFocus data-testid="login-email-input" placeholder="you@example.com or 98765 43210" value={form.identifier} onChange={set("identifier")} className="h-11 bg-white" />
              </div>
              {error && <p data-testid="auth-error" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
              <button type="submit" disabled={busy} data-testid="otp-send-button" className="btn-primary w-full">
                <MailCheck className="h-4 w-4" />{busy ? "Sending…" : "Send me a code"}
              </button>
            </form>
          ) : (
            <form onSubmit={verify} className="mt-10 space-y-5" data-testid="otp-form">
              <p className="text-sm text-[#5E5868]">Code sent {sent.kind === "phone" ? "by SMS to" : "to"} <strong className="text-[#26232E]" data-testid="otp-sent-to">{sent.to}</strong>.{" "}
                <button type="button" data-testid="otp-change-email-btn" onClick={() => { setSent(null); setError(""); }} className="font-medium text-[#3B5BDB] hover:underline">Change</button>
              </p>
              {sent.demo_code && (
                <div data-testid="otp-demo-banner" className="rounded-lg border border-dashed border-[#3B5BDB]/40 bg-[#E9EEFF]/70 p-3 text-sm">
                  <p className="font-medium text-[#26232E]">Demo mode — no SMS/email provider connected yet.</p>
                  <p className="mt-1 text-[#5E5868]">Your code is <span data-testid="otp-demo-code" className="font-mono text-base font-semibold tracking-[0.3em] text-[#3B5BDB]">{sent.demo_code}</span></p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="otp">6-digit code</Label>
                <Input id="otp" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6} required autoFocus data-testid="otp-code-input"
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••"
                  className="h-14 bg-white text-center font-mono text-2xl tracking-[0.5em]" />
              </div>
              {error && <p data-testid="auth-error" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
              <button type="submit" disabled={busy || code.length < 6} data-testid="otp-verify-button" className="btn-primary w-full">
                <KeyRound className="h-4 w-4" />{busy ? "Checking…" : isLogin ? "Log in" : "Create account"}
              </button>
              <button type="button" disabled={cooldown > 0 || busy} onClick={request} data-testid="otp-resend-button" className="w-full text-center text-sm text-[#5E5868] disabled:opacity-50">
                {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
              </button>
            </form>
          )}
          <p className="mt-6 text-sm text-[#5E5868]">
            {isLogin ? "New to Click, Print & Doodle? " : "Already have an account? "}
            <Link to={isLogin ? "/register" : "/login"} data-testid="auth-switch-link" className="font-medium text-[#3B5BDB] hover:underline">
              {isLogin ? "Create an account" : "Log in"}
            </Link>
          </p>
        </div>
      </div>
      <div className="relative hidden lg:block">
        <img src={SIDE} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/25" />
        <p className="absolute bottom-12 left-12 max-w-sm font-serif text-3xl italic text-white">"The pictures stay on the phone. The book stays in the family."</p>
      </div>
    </div>
  );
}
